import { Cell, flexRender } from "@tanstack/react-table";
import { memo } from "react";
import { getCellExternalPath } from "../utils/getCellExternalPath";
import { EntityLink } from "./EntityLink";

interface DataTableCellContentProps<TData extends { id: string }> {
  cell: Cell<TData, unknown>;
  isAggregated: boolean;
  firstVisibleColumnId?: string;
}

const DataTableCellContent = memo(
  <TData extends { id: string }>({
    cell,
    isAggregated,
    firstVisibleColumnId,
  }: DataTableCellContentProps<TData>) => {
    const table = cell.getContext().table;
    const meta = table.options.meta;

    const isFirstVisibleColumn =
      meta?.pathBuilder && cell.column.id === firstVisibleColumnId;

    if (isAggregated) {
      // Use custom aggregatedCell component if defined, otherwise use default cell renderer
      if (cell.column.columnDef.aggregatedCell) {
        return flexRender(
          cell.column.columnDef.aggregatedCell,
          cell.getContext(),
        );
      }
      // Fallback to displaying the aggregated value directly
      const aggregatedValue = cell.getValue();
      return aggregatedValue != null ? String(aggregatedValue) : "";
    }

    // Regular cell rendering
    const cellContent = flexRender(
      cell.column.columnDef.cell,
      cell.getContext(),
    );

    const externalTo = getCellExternalPath(cell);

    // Wrap first column with internal and optional external links if pathBuilder is provided
    if (isFirstVisibleColumn && meta?.pathBuilder) {
      return (
        <EntityLink
          internalTo={meta.pathBuilder(cell.row.original)}
          externalTo={externalTo}
        >
          {cellContent}
        </EntityLink>
      );
    }

    // Column with external link
    if (externalTo) {
      return <EntityLink externalTo={externalTo}>{cellContent}</EntityLink>;
    }

    return cellContent;
  },
) as <TData extends { id: string }>(
  props: DataTableCellContentProps<TData>,
) => JSX.Element;

export default DataTableCellContent;
